import React, { useState, useMemo, useEffect } from 'react';
// No need for useNavigate unless you're navigating away from this page
// import { useNavigate } from 'react-router-dom';
import { Table, Tbody, Td, Thead, Tr, Th } from 'react-super-responsive-table';
import { Card, CardBody, CardHeader, Col, Row, Button, ButtonGroup } from 'reactstrap';
import useAddRelativeComp from './useAddRelativeComp';
import { min } from 'lodash';
import useAddTmarTmprValue from './useAddTmarTmprValue';

// --- Configuration ---
// Set how many comparable properties to show at once
const COMPARABLES_PER_PAGE = 3;

const PreviewTable = ({ previewData, orderId,fileNumber }) => {
  console.log("previewData on preview table", previewData);

  const [currentPage, setCurrentPage] = useState(0);
  // setting the market value of 1st three
  const[marketVaue,setMarketValue] = useState(null);
  // custom hook for relative comp analysis
  const{isAdding, isAdded, addRelativeCompAnalysis} = useAddRelativeComp();

  // setting the tmar and tmpr value of selected comparables
  const[tmprValue,setTmprValue] = useState(null);
  const[tmarValue,setTmarValue] = useState(null);
  // custom hook for adding tmar and tmpr values
  const{isAddingValues,isAddedValues,addTmarAndTmprValues} = useAddTmarTmprValue();

  // Memoize derived data to prevent recalculations on every render
  const { subjectData, comparableData, totalPages, currentComparables } = useMemo(() => {
    if (!previewData || previewData.length === 0) {
      return { subjectData: null, comparableData: [], totalPages: 0, currentComparables: [] };
    }

    const subject = previewData[0];
    const comparables = previewData.slice(1); // All items after the first are comparables
    const pages = Math.ceil(comparables.length / COMPARABLES_PER_PAGE);

    const startIndex = currentPage * COMPARABLES_PER_PAGE;
    const endIndex = startIndex + COMPARABLES_PER_PAGE;
    const current = comparables.slice(startIndex, endIndex);

    const firstThreeComparables = comparables.slice(0,3);
    let totalSalePrice= 0;

    for (const item of firstThreeComparables){
        totalSalePrice += Number(item.sale_price) || 0;
      }
    const average = totalSalePrice / firstThreeComparables.length;
    const tenPercent = average * 0.10;
    const tenPercentLess = average - tenPercent;
    const tenPercentMore = average + tenPercent;

    const formattedRange = `${tenPercentLess.toLocaleString(undefined, { maximumFractionDigits: 0 })} - $${tenPercentMore.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    setMarketValue(formattedRange)
    
    // for getting tmpr value
    const tmprValue = Math.min(...comparables.map(item=>item.sale_price) || 0)
    console.log("tmpr value",tmprValue)
    setTmprValue(tmprValue)
    // for getting tmar value
    const tmarValue = Math.min(...comparables.map(item=>item.age_years) || 0)
    console.log("tmar value",tmarValue)
    setTmarValue(tmarValue)

    return {
      subjectData: subject,
      comparableData: comparables,
      totalPages: pages,
      currentComparables: current,
    };
  }, [previewData, currentPage]);

  useEffect(()=>{
    addRelativeCompAnalysis({"opinion_market_val":marketVaue,"order_id":orderId})
    addTmarAndTmprValues({"tmpr_predom":tmprValue,"tmar_predom":tmarValue,"order_id":orderId})
  },[marketVaue,tmprValue,tmarValue])

  if (!subjectData) {
    return (
      <Card>
        <CardHeader><h4>Preview Page</h4></CardHeader>
        <CardBody>
          <p>No preview data available.</p>
        </CardBody>
      </Card>
    );
  }

  const handleNext = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handlePrev = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  // Helper to generate a row for simple data points
  const renderSimpleRow = (label, dataKey) => {
    // format price
    const formatValue=(value)=>{
      if (dataKey=== 'sale_price' && value !== undefined && value !== null){
        const number = Number(value);
        return isNaN(number) ? "-" : `${number.toLocaleString('en-us')}`
      }

      // format price
    if(dataKey === 'sale_date' && value){
      const date = new Date(value);
      if(!isNaN(date)){
        const mm = String(date.getMonth() + 1).padStart(2,'0');
        const dd = String(date.getDate()).padStart(2,'0');
        const yyyy = date.getFullYear();
        return `${mm}/${dd}/${yyyy}`
      }
      return "-"
    }

      return value || "-"
    }

    return (
      <Tr key={dataKey}>
      <Td>{label}</Td>
      {/* Subject Data */}
      <Td>{formatValue(subjectData[dataKey] || "-")}</Td>
      {/* Comparable Data */}
      {currentComparables.map((item, index) => (
        <Td key={item.id || index}>{formatValue(item[dataKey] || "-")}</Td>
      ))}
    </Tr>
    )
  };

  return (
    <Card>
      <CardHeader><h4>Preview Page</h4></CardHeader>
      <CardBody>
        <Row className="mb-3 align-items-center">
          <Col md="6">
            <div style={{display:"flex", flexDirection:"row", gap:"2rem",alignItems:"flex-start", justifyContent:"space-between"}}>
              <h5>Sales Comparison Approach</h5>
              <h6>File Number: {fileNumber}</h6>
            </div>
          </Col>
          <Col md="6" className="d-flex justify-content-md-end">
            {/* --- Pagination Controls --- */}
            <div className='d-flex align-items-center'>
              <span className='me-3 text-muted'>
                {comparableData.length > 0 ? 
                 `Showing ${currentPage * COMPARABLES_PER_PAGE + 1} - ${Math.min((currentPage + 1) * COMPARABLES_PER_PAGE, comparableData.length)} of ${comparableData.length} comparables`
                 : 'No comparables'}
              </span>
              <ButtonGroup>
                <Button className="btn py-2" style={{ backgroundColor: "#00ADF0", color: "white", minWidth: "150px", fontWeight: "bolder" }}
                onClick={handlePrev} disabled={currentPage === 0}>
                  Previous
                </Button>
                <Button className="btn py-2" style={{ backgroundColor: "#00ADF0", color: "white", minWidth: "150px", fontWeight: "bolder" }}
                onClick={handleNext} disabled={currentPage >= totalPages - 1 || totalPages === 0}>
                  Next
                </Button>
              </ButtonGroup>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="table-responsive">
              <Table className='table table-bordered table-sm'>
                <Thead>
                  <Tr>
                    <Th style={{width:"260px"}}>Feature</Th>
                    <Th>Subject</Th>
                    {/* --- Dynamic Headers for Comparables --- */}
                    {currentComparables.map((item, index) => {
                      // Calculate the true number of the comparable
                      const comparableNumber = currentPage * COMPARABLES_PER_PAGE + index + 1;
                      return <Th key={item.id || index}>Comparable Sale #{comparableNumber}</Th>
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {/* --- Using the helper function for cleaner code --- */}
                  {renderSimpleRow("Address", "property_address")}
                  {renderSimpleRow("Proximity to Subject", "proximity")}
                  {renderSimpleRow("Sales Price", "sale_price")}
                  {renderSimpleRow("Price/Gross Living Area", "price_per_gross_living_area")}
                  {renderSimpleRow("Date of Sale", "sale_date")}
                  {renderSimpleRow("Location", "location")}
                  {renderSimpleRow("Site Size", "site_size")}
                  {renderSimpleRow("Site View", "site_view")}
                  {renderSimpleRow("Design (Style)", "design_style")}
                  {renderSimpleRow("Age (yrs.)", "age_years")}
                  {renderSimpleRow("Condition", "condition")}

                  {/* --- Special Row: Above Grade Room Count --- */}
                  <Tr>
                    <Td style={{ verticalAlign: 'middle' }}>
                      Above Grade
                      <br />
                      Room Count
                    </Td>
                    
                    {/* Subject Data Cell */}
                    <Td style={{ padding: 0 }}>
                      <Table className="table-bordered table-sm m-0" style={{ textAlign: 'center' }}>
                        <Tbody>
                          <Tr>
                            <Td>Total Rooms</Td>
                            <Td>Bedrooms</Td>
                            <Td>Bath(s)</Td>
                          </Tr>
                          <Tr>
                            <Td>{subjectData.total_rooms || "-"}</Td>
                            <Td>{subjectData.bedrooms || "-"}</Td>
                            <Td>{subjectData.baths || "-"}</Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Td>

                    {/* Dynamically render comparables */}
                    {currentComparables.map((item, index) => (
                      <Td key={item.id || index} style={{ padding: 0 }}>
                        <Table className="table-bordered table-sm m-0 text-center">
                          <Tbody>
                            <Tr>
                              <Td>Total Rooms</Td>
                              <Td>Bedrooms</Td>
                              <Td>Bath(s)</Td>
                            </Tr>
                            <Tr>
                              <Td>{item.total_rooms || '-'}</Td>
                              <Td>{item.bedrooms || '-'}</Td>
                              <Td>{item.baths || '-'}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Td>
                    ))}
                  </Tr>
                  
                  {/* --- Continue with simple rows --- */}
                  {renderSimpleRow("Gross Living Area", "gross_living_area")}
                  {renderSimpleRow("Basement", "basement")}
                  {renderSimpleRow("Heating/Cooling", "heat_cool")}
                  {renderSimpleRow("Garage/Carport", "garage_carport")}
                  {renderSimpleRow("Porch, Patio, Deck, etc.", "porch_patio")}

                  {/* --- Final Special Row --- */}
                  <Tr>
                    <Td><strong>Overall Comparison to Subject Property</strong></Td>
                    <Td>{subjectData.overall_comparison || "-"}</Td>
                    {currentComparables.map((item, index) => (
                      <Td key={item.id || index}>{item.overall_comparison || "-"}</Td>
                    ))}
                  </Tr>
                </Tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default PreviewTable;